import React from 'react';
import { GraduationCap, Code, Briefcase, Heart } from 'lucide-react';

const About = () => {
  const highlights = [
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: "Computer Science Student",
      description: "Final year at FAST-NUCES"
    },
    {
      icon: <Code className="w-6 h-6" />,
      title: "MERN Stack Developer",
      description: "Full-stack expertise in modern web development"
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: "Freelance Experience",
      description: "Creating responsive and SEO-friendly websites"
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "UI/UX Passionate",
      description: "Detail-oriented problem-solver"
    }
  ];

  return (
    <section id="about" className="py-20 bg-gray-800">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              About <span className="text-cyan-400">Me</span>
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-purple-500 mx-auto mb-8"></div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Passionate about creating digital solutions that make a difference
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Text Content */}
            <div className="space-y-6">
              <div className="prose prose-lg text-gray-300">
                <p className="text-lg leading-relaxed mb-6">
                  I'm a <span className="text-cyan-400 font-semibold">BS Computer Science student</span> at FAST-NUCES, 
                  currently in my Final year. My journey in software development is driven by curiosity and 
                  a passion for solving complex problems through elegant code.
                </p>
                
                <p className="text-lg leading-relaxed mb-6">
                  With a <span className="text-purple-400 font-semibold">strong foundation</span> in Data Structures, 
                  Object-Oriented Programming (C++), and modern web development technologies like React, Node.js, 
                  Express, and MongoDB, combined with expertise in <span className="text-purple-400 font-semibold">AI-integrated websites</span> and <span className="text-purple-400 font-semibold">data analysis</span>, I bring both theoretical knowledge and practical experience to every project.
                </p>
                
                <p className="text-lg leading-relaxed mb-6">
                  As a <span className="text-pink-400 font-semibold">freelance developer</span>, I've had the 
                  opportunity to work with various businesses, creating responsive and SEO-friendly websites 
                  that not only look great but also perform exceptionally well.
                </p>
                
                <p className="text-lg leading-relaxed">
                  I'm an <span className="text-cyan-400 font-semibold">introverted problem-solver</span> who 
                  finds joy in the details. My approach to development is methodical and user-focused, 
                  always keeping UI/UX at the forefront of my decision-making process.
                </p>
              </div>
            </div>

            {/* Right Side - Highlights Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {highlights.map((item, index) => (
                <div 
                  key={index}
                  className="group bg-gray-900/50 backdrop-blur-sm p-6 rounded-2xl border border-gray-700 hover:border-cyan-400/50 transition-all duration-300 hover:transform hover:scale-105"
                >
                  <div className="text-cyan-400 mb-4 group-hover:scale-110 transition-transform duration-300">
                    {item.icon}
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Stats Section */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-cyan-400 mb-2">5+</div>
              <div className="text-gray-300">Projects</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-purple-400 mb-2">2+</div>
              <div className="text-gray-300">Years Learning</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-pink-400 mb-2">12+</div>
              <div className="text-gray-300">Technologies</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-cyan-400 mb-2">∞</div>
              <div className="text-gray-300">Passion</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;